<script lang="ts">
export default {
  name: 'AccountExposureProperties',
};
</script>

<script setup lang="ts">
import { ref, computed, onBeforeMount, onBeforeUnmount } from 'vue';
import { Plus, Minus, File, FileSpreadsheet, CircleX, Check, TriangleAlert } from '@lucide/vue';
import { ExposureProperty, ExposureValue } from '@/types/AccountExposure';
import { accountService, referenceService } from '@/services/api';
import ChildAttributes from '@/views/account/ChildAttributes.vue';
import { compactMoney } from '@/utilities/Money';
import { percentage } from '@/utilities/Number';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import emptySrc from '@/assets/images/empty.svg';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { acceptContentTowerTypes } from '@/utilities/tower/Upload';
import { TransformWorksheetToProperties } from '@/utilities/ExposureProperties';
import AccountExposureDeleteModal from './AccountExposureDeleteModal.vue';
import PaginationController from '@/components/PaginationController.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Spinner from '@/components/v2/Spinner.vue';
import Progress from '@/components/v2/Progress.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { DateTime } from 'luxon';
import { toDateTime } from '../../utilities/Dates';
import { filters } from '@/utilities/Filters';
import { useRoute, useRouter } from 'vue-router';

const $route = useRoute();
const $router = useRouter();

// -----
// data
// -----
const templatePath = '/downloads/CatModeling_Import.xlsx';

const loading = ref(true);
const properties = ref([] as (ExposureProperty & { opened: boolean; muted: boolean })[]);
const isFileValid = ref(false);
const pageSize = ref(50);
const exposurePropertyCount = ref(0);
const isUploading = ref(false);
const acceptContentTypes = ref(acceptContentTowerTypes);
const binaryData = ref('');
const filename = ref('');
const isValidating = ref(false);
const file = ref(undefined as undefined | File);
const validationErrors = ref([] as string[]);
const uploadProgress = ref(0);
const uploadErrors = ref([] as string[]);
const uploadedProperties = ref([] as ExposureProperty[]);
const exposureRefData = ref(new Map<string, number>());
const fileInput = ref<HTMLInputElement | null>(null);

// ---------
// computed
// ---------
const currentPage = computed(() => {
  const page = parseInt('' + $route.query.page);
  return isNaN(page) ? 1 : page;
});

const offset = computed(() => {
  return Math.max(0, (currentPage.value - 1) * pageSize.value);
});

// -----------
// lifecycles
// -----------
onBeforeMount(async () => {
  await refreshData();
});

onBeforeUnmount(() => {
  $router.replace({ path: $route.path, query: { ...$route.query, page: undefined } });
});

// ----------
// functions
// ----------
async function refreshData() {
  loading.value = true;
  exposurePropertyCount.value = await accountService.exposurePropertiesCount($route.params.id as string);
  const exposureProperties = await accountService.exposureProperties($route.params.id as string, offset.value, pageSize.value);
  properties.value = (exposureProperties ?? []).map((p) => ({ ...p, opened: false, muted: false }));

  // shape ref data into a map with key like 'buildingShape--Square' -> corresponding id value for easy lookups
  const refData = await referenceService.exposurePropertyRefData();
  refData.forEach((data) => {
    data.values.forEach((v) => {
      exposureRefData.value.set(`${data.category}--${v.name}`, v.id);
    });
  });

  loading.value = false;
}

function toggleChild(id: string) {
  const willClose = properties.value.some((p) => p.id === id && p.opened);
  properties.value = properties.value.slice().map((p) => {
    if (p.id === id) {
      return { ...p, opened: !p.opened, muted: false };
    }
    return { ...p, opened: false, muted: !willClose };
  });
}

function miscellaneousAttributes(p: ExposureProperty): { name: string; value: string | undefined }[] {
  return [
    { name: 'Renewables', value: p.renewables },
    { name: 'User Defined 5', value: p.userDefined5 },
    { name: 'User Defined 4', value: p.userDefined4 },
    { name: 'User Defined 3', value: p.userDefined3 },
  ];
}

function primaryCharacteristicAttributes(p: ExposureProperty): { name: string; value: string | number | undefined }[] {
  return [
    { name: 'Year Built', value: p.yearBuilt },
    { name: 'Stories', value: p.stories },
    {
      name: 'Square Footage',
      value: p.squareFootage ? p.squareFootage.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : undefined,
    },
    { name: 'Construction', value: p.construction },
    { name: 'Occupancy', value: p.occupancy },
  ];
}

function deductibleAttributes(p: ExposureProperty): { name: string; value: string | number | undefined }[] {
  return ['hu', 'eq', 'st', 'if', 'wf', 'ws']
    .map((prefix) => {
      const amount = Reflect.get(p, `${prefix}DeductibleAmount`);
      const percent = Reflect.get(p, `${prefix}DeductiblePercent`);
      return [
        { name: `${prefix.toUpperCase()} Amount`, value: compactMoney(amount) },
        { name: `${prefix.toUpperCase()} Percent`, value: percent ? percentage(percent * 100, 1, 2) : undefined },
      ];
    })
    .flat();
}

function secondaryAttributes(p: ExposureProperty): { name: string; value: string | number | undefined }[] {
  return [
    { name: 'Roof Geometry', value: p.roofGeometry },
    { name: 'Roof Covering', value: p.roofCovering },
    { name: 'Roof Anchorage', value: p.roofAnchorage },
    { name: 'Roof Built', value: p.roofYearBuilt },
    { name: 'Window Protection', value: p.windowProtection },
    { name: 'Wall Type', value: p.wallType },
    { name: 'Wall Siding', value: p.wallSiding },
    { name: 'Building Shape', value: p.buildingShape },
    { name: 'Foundation Type', value: p.foundationType },
  ];
}

async function uploadProperties(): Promise<boolean> {
  isUploading.value = true;
  const numProperties = uploadedProperties.value.length;

  const chunksize = 1000;

  // show start of progress, if only 1 call to be made, set it to 10%
  uploadProgress.value = chunksize > numProperties ? 10 : (chunksize / numProperties) * 100;

  // wipe out all records first (this line may likely be removed in the future)
  await accountService.deleteAllExposureProperty($route.params.id as string);
  uploadProgress.value = (chunksize / numProperties) * 100;

  for (let i = 0; i < numProperties; i += chunksize) {
    uploadProgress.value = ((i + chunksize) / numProperties) * 100;
    const chunk = uploadedProperties.value.slice(i, i + chunksize > numProperties ? numProperties : i + chunksize);
    try {
      let errs = await new Promise<string[]>((resolve) => {
        setTimeout(async () => {
          resolve(await accountService.uploadExposureProperties($route.params.id as string, chunk));
        }, 500);
      });

      if (errs.length > 0) {
        uploadErrors.value.push(...errs);
        break;
      }
    } catch (e) {
      uploadErrors.value.push('An unexpected error occurred');
      break;
    }
  }

  isUploading.value = false;
  await refreshData();

  if (uploadErrors.value.length > 0) {
    isFileValid.value = false;
    return false;
  }

  resetFile();
  $router.replace({ path: $route.path, query: { ...$route.query, page: '1' } });
  return true;
}

function resetFile() {
  if (!isValidating.value && !isUploading.value) {
    binaryData.value = '';
    file.value = undefined;
    filename.value = '';
    validationErrors.value = [];
    uploadErrors.value = [];
    uploadedProperties.value = [];
    isFileValid.value = false;
    uploadProgress.value = 0;
  }
}

function selectFile() {
  fileInput.value?.click();
}

function readFile(event: Event) {
  if (!event || !event.target) {
    return;
  }

  const files = (event.target as HTMLInputElement).files;
  if (!files || files.length === 0) {
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    try {
      binaryData.value = reader.result as string;
      filename.value = files[0].name;
      file.value = files[0];
      isValidating.value = true;

      setTimeout(() => {
        const { properties, errors } = TransformWorksheetToProperties(binaryData.value, exposureRefData.value);
        isValidating.value = false;
        isFileValid.value = errors.length === 0;
        validationErrors.value = errors;
        uploadedProperties.value = properties;
      }, 200);
    } catch (err) {
      console.error(err);
    } finally {
      fileInput.value = null;
    }
  };
  reader.readAsArrayBuffer(files[0]);
}

async function deleteExposureValue(entry: ExposureProperty | ExposureValue): Promise<unknown> {
  await accountService.deleteExposureProperty($route.params.id as string, 'id' in entry ? entry.id : 0);
  // if the deletion caused the current page to not exist, then go to the last page.
  const totalPages = Math.ceil((exposurePropertyCount.value - 1) / pageSize.value);
  if (currentPage.value > totalPages) {
    return $router.replace({ path: $route.path, query: { ...$route.query, page: '' + totalPages } });
  }
  // otherwise, just reload the current page
  return refreshData();
}
</script>

<template>
  <div>
    <div style="float: right">
      <ConfirmModal
        title="Upload Properties From Template"
        size="md"
        :action="uploadProperties"
        :dismiss="resetFile"
        confirmText="Upload"
        :isConfirmDisabled="() => !isFileValid"
        :keep-open="isUploading || uploadErrors.length > 0"
        :submitLoading="isUploading">
        <template v-slot:button="{ props }">
          <AppButton
            primary
            class="mx-3 mt-1 mb-1 rounded-full"
            data-test="upload-properties-button"
            v-bind="props">
            Upload Properties
            <Plus
              :size="16"
              class="ml-1" />
          </AppButton>
        </template>
        <div>
          Use the template linked below to upload properties.

          <div class="mt-2">
            <a
              :href="templatePath"
              class="flex items-center">
              <FileSpreadsheet
                :size="18"
                class="mr-1" />
              Cat Modeling Template
            </a>
          </div>
          <hr class="mt-4 mb-4" />
          <AppButton
            v-if="!binaryData"
            class="border border-gray-300"
            data-test="select-file"
            @click="selectFile">
            Select File
          </AppButton>

          <div
            class="flex items-center"
            v-else>
            <File
              :size="18"
              class="mr-1"
              style="vertical-align: middle" />
            <span style="vertical-align: middle">{{ filename }}</span>

            <button
              v-if="!isValidating"
              @click="resetFile"
              class="mx-2">
              <CircleX :size="18" />
            </button>

            <Spinner
              v-if="isValidating"
              size="xs"
              class="ml-1" />

            <template v-else>
              <Check
                v-if="isFileValid"
                :size="18"
                class="text-green-600" />

              <span
                v-if="validationErrors.length > 0"
                class="text-error">
                <TriangleAlert
                  :size="18"
                  class="" />
              </span>
            </template>
          </div>

          <input
            ref="fileInput"
            type="file"
            @change="readFile"
            :accept="acceptContentTypes"
            style="display: none" />

          <div
            class="property-validation-errors"
            v-if="validationErrors.length > 0 && !isValidating">
            <span class="text-error">Cannot upload due to the following:</span>
            <ul>
              <li
                v-for="(error, index) of validationErrors"
                :key="index">
                {{ error }}
              </li>
            </ul>
          </div>

          <div
            class="mt-2 mb-2"
            v-if="uploadProgress > 0">
            <Progress
              :value="uploadProgress"
              :max="100" />
          </div>

          <div
            class="property-upload-errors"
            v-if="uploadErrors.length > 0">
            <span class="text-error">Upload failed due to the following:</span>
            <ul>
              <li
                v-for="(error, index) of uploadErrors"
                :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </ConfirmModal>
    </div>

    <EmptyDisplay
      :src="emptySrc"
      title="No Exposures"
      text="No property exposures exist for this client."
      v-if="!loading && properties.length === 0" />

    <div
      v-if="loading"
      class="mt-4 space-y-2 p-2">
      <Skeleton
        v-for="i in 8"
        :key="i"
        shape="rectangle"
        class="w-full"
        height="44px" />
    </div>
    <div v-if="!loading && properties.length > 0">
      <h2 class="mx-3 mt-3 mb-6">Exposure Data</h2>
      <div class="table-hscroller">
        <table class="apptable dense">
          <thead>
            <tr>
              <td>&nbsp;</td>
              <td
                class="text-center"
                colspan="2">
                Geographic
              </td>
              <td
                class="text-center hbl hbr"
                colspan="4">
                Replacement Values
              </td>
              <td colspan="3">&nbsp;</td>
            </tr>
            <tr>
              <td>&nbsp;</td>
              <td>Name</td>
              <td class="hbr">Address</td>
              <td class="number">Building</td>
              <td class="number">Other</td>
              <td class="number">Contents</td>
              <td class="number hbr">BI</td>
              <td class="date">Uploaded On</td>
              <td>Uploaded By</td>
              <td></td>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="p in properties"
              :key="p.id">
              <tr
                class="parent-row group"
                :class="{ opened: p.opened, muted: p.muted }"
                @click="toggleChild(p.id)">
                <td>
                  <AppButton
                    class="inline-flex min-w-0 p-1"
                    @click.stop="toggleChild(p.id)"
                    :aria-expanded="p.opened">
                    <Minus
                      v-if="p.opened"
                      :size="14" />
                    <Plus
                      v-else
                      :size="14" />
                  </AppButton>
                </td>
                <td>{{ p.locationName ?? '--' }}</td>
                <td>
                  <div v-if="p.address1">{{ p.address1 }}</div>
                  <div>
                    <span v-if="p.city">{{ p.city }}</span>
                    <span
                      class="d-inline-block mr-1"
                      v-if="p.city && (p.state || p.postalCode)"
                      >,</span
                    >
                    <span v-if="p.state">{{ p.state }}</span>
                    <span
                      class="d-inline-block mr-1"
                      v-if="p.state && p.postalCode"></span>
                    <span v-if="p.postalCode">{{ p.postalCode }}</span>
                    <div v-if="p.county">{{ p.county }} County</div>
                    <div v-if="p.latitude && p.longitude">{{ p.latitude }}, {{ p.longitude }}</div>
                  </div>
                </td>
                <td class="number">{{ filters.compactMoney(p.buildingValue) ?? '--' }}</td>
                <td class="number">{{ filters.compactMoney(p.otherPropertyValue) ?? '--' }}</td>
                <td class="number">{{ filters.compactMoney(p.contentsValue) ?? '--' }}</td>
                <td class="number">{{ filters.compactMoney(p.businessInterruptionValue) ?? '--' }}</td>
                <td class="date">{{ toDateTime(p.updatedAt as Date).toLocaleString(DateTime.DATETIME_SHORT) }}</td>
                <td>{{ p.updatedBy ?? '--' }}</td>
                <td class="actions">
                  <div
                    class="actions-div inline-flex items-center gap-1 pr-2"
                    data-test="actions-div">
                    <AccountExposureDeleteModal
                      :exposure="p as ExposureProperty"
                      :delete="deleteExposureValue"
                      :isPropertyExposure="true"
                      trigger-class="property-action-icon inline-flex cursor-pointer rounded p-1 opacity-30 group-hover:opacity-100"
                      :data-test="`${p.id}-delete-btn`" />
                  </div>
                </td>
              </tr>
              <tr
                class="child-row"
                :class="{ opened: p.opened }">
                <td colspan="11">
                  <div class="child-details">
                    <div>
                      <ChildAttributes
                        title="Primary Characteristics"
                        :attributes="primaryCharacteristicAttributes(p)" />
                      <br />
                      <ChildAttributes
                        title="Miscellaneous"
                        :attributes="miscellaneousAttributes(p)" />
                    </div>
                    <ChildAttributes
                      title="Deductibles"
                      :attributes="deductibleAttributes(p)" />
                    <ChildAttributes
                      title="Secondary Characteristics"
                      :attributes="secondaryAttributes(p)" />
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <PaginationController
        :page="currentPage"
        :pageSize="pageSize"
        :totalSize="exposurePropertyCount"
        @refresh="refreshData" />
    </div>
  </div>
</template>

<style scoped>
table.apptable > thead > tr:nth-child(1) > td {
  background: #e0e0e0;
}

.hbl {
  border-left: 1px solid #bdbdbd;
}

.hbr {
  border-right: 1px solid #bdbdbd;
}

tr.muted {
  opacity: 0.5;
}

tr {
  cursor: pointer;
}

td > td {
  user-select: none;
}

.child-row {
  display: none;
}

.parent-row.opened > td {
  background: #eeeeee;
}

.parent-row.opened > td:nth-child(1),
.child-row.opened > td:nth-child(1) {
  border-left: 10px solid #bdbdbd;
}

.child-row.opened {
  display: table-row;
}

.child-details {
  display: grid;
  grid-template-columns: repeat(3, auto);
  grid-gap: 2rem;
}

.child-row > td {
  background-color: #f7f7f7;
}

.child-row > td > div {
  padding: 1rem;
}

hr {
  border: 0;
  height: 1px;
  background: #cccccc;
}

/* Delete action: low opacity by default, full on row hover + icon hover (same as AccountExposure.vue) */
.parent-row > td.actions .actions-div :deep(.property-action-icon) {
  transition:
    opacity 0.15s ease,
    background-color 0.15s ease;
}

.parent-row > td.actions .actions-div :deep(.property-action-icon:hover) {
  background-color: rgba(0, 0, 0, 0.1);
  opacity: 1;
}

@media (pointer: none), (pointer: coarse) {
  .parent-row > td.actions .actions-div :deep(button) {
    opacity: 1;
  }
}
</style>
